// =============================================================================
// src/features/explore/explore-map.ts — Mapa Leaflet do Amazonas (porte ExploreMap).
//
// Proveniencia: assets/explore.js `var ExploreMap = {...}` (linhas 91-395). Porte
//   FIEL: mapa estilo Google Maps cobrindo os 62 municipios do AM com tiles do
//   OpenStreetMap (pedido 2026-10-13: Explorar deixou o CartoDB light legado,
//   reintroduzido em 53855db); zoom >= 9 sobre Manaus revela as 6 zonas. Centro
//   [-4.6,-64.5] zoom 6,
//   zoomSnap/zoomDelta 0.5, wheelPxPerZoomLevel 120, flyToBounds duration 1.0,
//   jitter espiral (angulo 2.39996), thresholds de densidade por quartil — TODOS
//   sao paridade visual e NAO devem ser alterados.
//
// Dados: src/data (EXPLORE_DATASET, MANAUS_*, MANAUS_ELECTORAL_BY_YEAR) — NUNCA o
//   global EXPLORE_AM/DATA. Leaflet via `import L` + @types/leaflet 1.9.x; API
//   (L.map/geoJSON/divIcon/layerGroup/control.zoom/flyToBounds/latLngBounds)
//   verificada via Context7 (/leaflet/leaflet) em 2026-05-30.
//
// Acoplamento com a UI: o legado chamava ExploreUI.* direto. Aqui a UI e injetada
//   como ExploreMapUiPort (DI), evitando import circular em nivel de modulo.
// =============================================================================

import L from 'leaflet';
import type {
  Map as LeafletMap,
  GeoJSON as LeafletGeoJSON,
  LayerGroup as LeafletLayerGroup,
  Layer,
  Marker,
  LatLng,
  LatLngBounds,
  LeafletMouseEvent,
  PathOptions,
  GeoJSONOptions,
} from 'leaflet';
import type {
  ZonaKey,
  MunicipalityId,
  AmazonasRegion,
  ElectoralYear,
  ExplorePoiCategory,
  ExploreCampaignMarkerKind,
  AtalaiaClient,
} from '../../app/types';
import { hydrateExploreLive } from './explore-live-hydrate';
import {
  EXPLORE_DATASET,
  MANAUS_ZONA_DISPLAY_NAME,
  MANAUS_MUN_NAMES,
} from '../../data';
import {
  GEO_MUN_URL,
  GEO_TSE_ZONES_URL,
  GEO_BAIRROS_URL,
  GEO_CONTOUR_URL,
  STATE_OUTLINE_STYLE,
  MANAUS_ID,
  EXPLORE_ZOOM_THRESHOLD,
  MAJOR_LABEL_IDS,
  type ExploreColorMetric,
} from './constants';
import {
  prefersReducedMotion,
  munGeoFeatureId,
  bairroZonaKey,
} from './explore-helpers';
import {
  munPathStyle,
  zonePathStyle,
  buildExploreLegend,
  type ExploreStyleState,
  type ExploreLegend,
} from './explore-styling';
import { buildPoiGroups, buildAcoesGroup, buildCampaignGroups } from './explore-markers';
import { OSM_TILE_URL, OSM_ATTRIBUTION, OSM_MAX_ZOOM } from '../../core/osm-tiles';
import type { ExploreVoteViz } from './explore-vote-constants';
import { ExploreVoteLayer, type VoteLevel } from './explore-vote-layer';
import { ExploreTseZoneLayer, fetchTseZones, EXPLORE_TALL_POPUP_OPTIONS } from './explore-tse-zone-layer';
import { munDistribution, zoneDistribution } from './explore-vote-data';
import { buildVoteRankingHtml } from './explore-vote-popup';
import { polygonCentroidOf } from './explore-geo';
import { ExploreG26Mode } from './explore-g26-mode';
import { G26_STORE } from './explore-g26-store';
import type { G26CargoCode } from './explore-g26-types';
import { deriveG26VoteRows, g26IndexOfId } from './explore-g26-derive';

/** Re-export do bloco de legenda (forma publica estavel para a UI). */
export type { ExploreLegend } from './explore-styling';

/** Tipo de entidade selecionada no mapa (municipio do AM ou zona de Manaus). */
export type ExploreSelectionKind = 'mun' | 'zone';

/**
 * Porta minima da UI consumida pelo mapa (DI). A implementacao concreta
 * (ExploreUI) e injetada por bindExploreUi para quebrar o ciclo map<->ui.
 */
export interface ExploreMapUiPort {
  refresh(): void;
  showCard(kind: ExploreSelectionKind, id: string): void;
  hideCard(): void;
}

/** Feature de municipio do GeoJSON do AM (properties.id/name). */
type MunFeature = GeoJSON.Feature<GeoJSON.Geometry, { id: MunicipalityId; name: string }>;
/** Feature de bairro de Manaus (properties.ZONAS). */
type BairroFeature = GeoJSON.Feature<GeoJSON.Geometry, { ZONAS: string }>;
/** GeoJSON layer com a feature anexada (Leaflet popula `.feature` em cada layer). */
type FeaturedLayer<F> = L.GeoJSON & { feature: F };

/**
 * Controlador do mapa Leaflet da tela Explorar. Singleton exportado ao final
 * (ExploreMap) — mesma forma de uso do `var ExploreMap` legado, agora tipado.
 */
class AmazonasExploreMap {
  ready = false;
  // Reforma 2026-06 (D2): abre PINTADO por partido — a landing vazia ("Nenhuma
  // (limpo)") era o maior tell de amadorismo do produto. 'nenhum' segue
  // disponivel no painel Camadas; voteViz continua forcando 'nenhum' (608-612).
  colorBy: ExploreColorMetric = 'partido';
  year: ElectoralYear = '2024';
  party: string | null = null;
  region: AmazonasRegion | null = null;
  // diverge do golden por decisao de produto 2026-05-31: recortes municipais novos.
  micro: string | null = null;
  eleitoradoBand: string | null = null;
  runnerUp: string | null = null;
  // Overlay de votos por candidato (inspirado no Datapedia). 'nenhum' = desligado.
  voteViz: ExploreVoteViz = 'nenhum';
  candidate: string | null = null;
  selectedKind: ExploreSelectionKind | null = null;
  selectedId: string | null = null;

  private map: LeafletMap | null = null;
  private munLayer: LeafletGeoJSON | null = null;
  private zoneLayer: LeafletGeoJSON | null = null;
  private stateOutline: LeafletGeoJSON | null = null;
  private selectedLayer: Layer | null = null;
  private ui: ExploreMapUiPort | null = null;
  // Cliente AtalaIA injetado (DI); null = integracao ao vivo desligada (so baseline).
  private atalaiaClient: AtalaiaClient | null = null;

  private munCentroids = new Map<MunicipalityId, LatLng>();
  private zoneCentroids = new Map<ZonaKey, LatLng>();
  private munBounds = new Map<MunicipalityId, LatLngBounds>();
  private regionBounds = new Map<AmazonasRegion, LatLngBounds>();
  private munLabelMarkers: Marker[] = [];
  private zoneLabelMarkers: Marker[] = [];
  private poiGroups = new Map<ExplorePoiCategory, LeafletLayerGroup>();
  private campaignGroups = new Map<ExploreCampaignMarkerKind, LeafletLayerGroup>();
  private acoesGroup: LeafletLayerGroup | null = null;
  private voteLayer = new ExploreVoteLayer();
  // Modo "Eleicoes Gerais 2026" (TSE oficial, pedido 2026-10-13) — estado e camadas
  // novas vivem no delegado; aqui so os ganchos de render/clique/zoom.
  readonly g26 = new ExploreG26Mode(G26_STORE);
  // Camada "Zonas eleitorais (TSE)" — criada no 1o toggle (asset sob demanda, 2026-10-13).
  private tseZoneLayer: ExploreTseZoneLayer | null = null;
  // Nivel ativo do overlay de votos (municipios x zonas), sincronizado com o zoom.
  private voteLevel: VoteLevel = 'mun';
  private densThresholds: readonly [number, number, number] = [10, 40, 90];
  private popThresholds: readonly [number, number, number] = [15, 30, 60];

  /** Injeta a UI (chamado por bindExploreUi no boot da feature). */
  bindUi(ui: ExploreMapUiPort): void {
    this.ui = ui;
  }

  /** Injeta o cliente AtalaIA (DI) p/ a hidratacao ao vivo; null = so baseline estatico. */
  bindAtalaia(client: AtalaiaClient | null): void {
    this.atalaiaClient = client;
  }

  /** Cria o mapa na 1a abertura; nas seguintes apenas invalida o tamanho. */
  ensure(): void {
    if (this.ready) { this.invalidate(); return; }
    this.ready = true;
    this.createMap();
    void this.loadGeoAndBuild();
  }

  /** Instancia o L.map com o basemap OpenStreetMap + zoom control (assets/explore.js 107-118). */
  private createMap(): void {
    this.map = L.map('exploreMap', {
      center: [-4.6, -64.5], zoom: 6, minZoom: 5, maxZoom: 14,
      zoomControl: false, attributionControl: false,
      zoomSnap: 0.5, zoomDelta: 0.5, wheelPxPerZoomLevel: 120,
    });
    L.tileLayer(OSM_TILE_URL, { maxZoom: OSM_MAX_ZOOM, attribution: OSM_ATTRIBUTION }).addTo(this.map);
    L.control.zoom({ position: 'topright' }).addTo(this.map);
    this.map.on('click', () => { this.clearSelection(); this.ui?.hideCard(); });
    this.map.on('zoomend moveend', () => this.updateForZoom());
  }

  /** Busca os GeoJSON (contorno tolerante a falha), hidrata ao vivo, monta e enquadra. */
  private async loadGeoAndBuild(): Promise<void> {
    // GeoJSON e Hasura buscam em PARALELO; ambos aguardados antes de calcular quartis/
    // pintar — sem corrida (overlay ao vivo ja mesclado quando buildMunis le o dataset).
    const geoPromise = Promise.all([
      fetch(GEO_MUN_URL).then((r) => r.json()),
      fetch(GEO_BAIRROS_URL).then((r) => r.json()),
      fetch(GEO_CONTOUR_URL).then((r) => r.json()).catch(() => null),
    ]);
    await hydrateExploreLive(this.atalaiaClient);
    const [munGeo, bairrosGeo, contourGeo] = await geoPromise;
    this.densThresholds = this.quartilesOf((m) => m.eleitoresK, this.densThresholds);
    this.popThresholds = this.quartilesOf((m) => m.habK, this.popThresholds);
    this.buildMunis(munGeo);
    this.buildZones(bairrosGeo);
    this.buildStateOutline(contourGeo);
    this.buildMarkers();
    this.fitState();
    this.updateForZoom();
    this.render();
    this.ui?.refresh();
  }

  /** Quartis [0.25/0.5/0.8] de um campo numerico dos municipios (assets/explore.js 135-144). */
  private quartilesOf(
    pick: (m: typeof EXPLORE_DATASET.munis[MunicipalityId]) => number,
    fallback: readonly [number, number, number],
  ): readonly [number, number, number] {
    const values = Object.values(EXPLORE_DATASET.munis)
      .map(pick)
      .filter((v): v is number => v != null)
      .sort((a, b) => a - b);
    if (!values.length) return fallback;
    const quantile = (p: number): number => values[Math.min(values.length - 1, Math.floor(p * values.length))] as number;
    return [quantile(0.25), quantile(0.5), quantile(0.8)];
  }

  /**
   * Adiciona o contorno do estado (limite vermelho tracejado estilo Google Maps).
   * interactive:false deixa o clique atravessar para os municipios; fica sempre
   * visivel (nao entra em showZoneLevel/showMunLevel).
   * diverge do golden por decisao de produto 2026-05-31.
   */
  private buildStateOutline(contourGeo: unknown): void {
    if (!contourGeo || !this.map) return;
    this.stateOutline = L.geoJSON(contourGeo as GeoJSON.GeoJsonObject, {
      interactive: false,
      style: () => STATE_OUTLINE_STYLE,
    }).addTo(this.map);
  }

  /** Snapshot do estado que afeta cor/estilo/legenda (delega a explore-styling). */
  private styleState(): ExploreStyleState {
    return {
      colorBy: this.colorBy, year: this.year, party: this.party, region: this.region,
      micro: this.micro, eleitoradoBand: this.eleitoradoBand, runnerUp: this.runnerUp,
      densThresholds: this.densThresholds, popThresholds: this.popThresholds,
      g26: this.g26.selection(),
    };
  }

  /** PathOptions de um municipio (delega ao calculo puro com o snapshot atual). */
  private munStyle(id: MunicipalityId): PathOptions {
    return munPathStyle(id, this.styleState());
  }

  /** PathOptions de uma zona (delega ao calculo puro com o snapshot atual). */
  private zoneStyle(key: ZonaKey): PathOptions {
    return zonePathStyle(key, this.styleState());
  }

  // ── camada de municipios ───────────────────────────────────────────────────

  /** Monta a camada de municipios + centroides/bounds + rotulos das cidades-chave. */
  private buildMunis(munGeo: unknown): void {
    const options: GeoJSONOptions = {
      style: (f) => this.munStyle(munGeoFeatureId(f as MunFeature)),
      onEachFeature: (feature, layer) => this.bindMunFeature(feature as MunFeature, layer),
    };
    this.munLayer = L.geoJSON(munGeo as GeoJSON.GeoJsonObject, options);
    this.indexMunGeometry();
    this.buildMajorLabels();
  }

  /** Tooltip + hover/click de um municipio (assets/explore.js 157-167). */
  private bindMunFeature(feature: MunFeature, layer: Layer): void {
    const id = feature.properties.id;
    layer.bindTooltip(feature.properties.name, { direction: 'top', sticky: true, className: 'exp-tip' });
    layer.on('mouseover', (e: LeafletMouseEvent) => this.onMunHover(e));
    layer.on('mouseout', (e: LeafletMouseEvent) => { if (e.target !== this.selectedLayer) this.munLayer?.resetStyle(e.target); });
    layer.on('click', (e: LeafletMouseEvent) => this.onMunClick(e, id));
  }

  /** Realca o municipio sob o cursor (exceto o selecionado) e o traz pra frente. */
  private onMunHover(e: LeafletMouseEvent): void {
    if (e.target !== this.selectedLayer) e.target.setStyle({ weight: 1.6, color: '#1F1F1F', fillOpacity: 0.74 });
    e.target.bringToFront();
  }

  /** Clique em municipio: com overlay ativo abre o popup de ranking; senao, o card. */
  private onMunClick(e: LeafletMouseEvent, id: MunicipalityId): void {
    L.DomEvent.stopPropagation(e);
    this.select('mun', id, e.target);
    if (this.voteViz !== 'nenhum') this.openVotePopup('mun', id);
    else this.ui?.showCard('mun', id);
  }

  /**
   * Calcula centroides/bounds por municipio e acumula os bounds por regiao. O
   * centroide vem do POLIGONO real (forma) com fallback no centro do bounds — alinha
   * a bolha de voto ao centro visual do municipio (formas irregulares do AM).
   */
  private indexMunGeometry(): void {
    this.munLayer?.eachLayer((layer) => {
      const feature = (layer as FeaturedLayer<MunFeature>).feature;
      const bounds = (layer as L.GeoJSON).getBounds();
      const centroid = polygonCentroidOf(feature.geometry);
      this.munCentroids.set(feature.properties.id, centroid ? L.latLng(centroid[0], centroid[1]) : bounds.getCenter());
      this.munBounds.set(feature.properties.id, bounds);
      const region = EXPLORE_DATASET.munis[feature.properties.id]?.region;
      if (region) this.extendRegionBounds(region, bounds);
    });
  }

  /** Estende (criando se preciso) o bounds acumulado de uma regiao. */
  private extendRegionBounds(region: AmazonasRegion, bounds: LatLngBounds): void {
    const acc = this.regionBounds.get(region) ?? L.latLngBounds([]);
    acc.extend(bounds);
    this.regionBounds.set(region, acc);
  }

  /** Cria os rotulos permanentes das cidades-chave (assets/explore.js 185-193). */
  private buildMajorLabels(): void {
    MAJOR_LABEL_IDS.forEach((id) => {
      const center = this.munCentroids.get(id);
      if (!center) return;
      const name = EXPLORE_DATASET.munis[id]?.name ?? MANAUS_MUN_NAMES[id] ?? '';
      const className = 'exp-mun-label' + (id === MANAUS_ID ? ' is-capital' : '');
      this.munLabelMarkers.push(this.makeLabelMarker(center, className, name));
    });
  }

  /** Marcador-rotulo nao-interativo no tooltipPane (divIcon de span). */
  private makeLabelMarker(center: LatLng, className: string, name: string): Marker {
    return L.marker(center, {
      icon: L.divIcon({ className, html: `<span>${name}</span>`, iconSize: [0, 0] }),
      interactive: false,
      pane: 'tooltipPane',
    });
  }

  // ── camada de zonas (close-up de Manaus) ────────────────────────────────────

  /** Monta a camada de zonas de Manaus + rotulos por zona (assets/explore.js 198-231). */
  private buildZones(bairrosGeo: unknown): void {
    const options: GeoJSONOptions = {
      style: (f) => this.zoneStyle(bairroZonaKey(f as BairroFeature)),
      onEachFeature: (feature, layer) => this.bindZoneFeature(feature as BairroFeature, layer),
    };
    this.zoneLayer = L.geoJSON(bairrosGeo as GeoJSON.GeoJsonObject, options);
    this.buildZoneLabels();
  }

  /** Hover/click de um bairro -> seleciona a zona e abre o card (assets/explore.js 202-214). */
  private bindZoneFeature(feature: BairroFeature, layer: Layer): void {
    const key = bairroZonaKey(feature);
    layer.on('mouseover', (e: LeafletMouseEvent) => this.onZoneHover(e));
    layer.on('mouseout', (e: LeafletMouseEvent) => { if (e.target !== this.selectedLayer) this.zoneLayer?.resetStyle(e.target); });
    layer.on('click', (e: LeafletMouseEvent) => this.onZoneClick(e, key));
  }

  /** Realca o bairro sob o cursor (exceto o selecionado). */
  private onZoneHover(e: LeafletMouseEvent): void {
    if (e.target !== this.selectedLayer) e.target.setStyle({ weight: 2, color: '#1F1F1F', fillOpacity: 0.78 });
    e.target.bringToFront();
  }

  /** Clique em zona: com overlay ativo abre o popup de ranking; senao, o card da zona. */
  private onZoneClick(e: LeafletMouseEvent, key: ZonaKey): void {
    L.DomEvent.stopPropagation(e);
    this.select('zone', key, e.target);
    if (this.voteViz !== 'nenhum') this.openVotePopup('zone', key);
    else this.ui?.showCard('zone', key);
  }

  /** Agrega os bounds por zona e cria um rotulo central por zona (assets/explore.js 216-230). */
  private buildZoneLabels(): void {
    const zoneBounds = new Map<ZonaKey, LatLngBounds>();
    this.zoneLayer?.eachLayer((layer) => {
      const key = bairroZonaKey((layer as FeaturedLayer<BairroFeature>).feature);
      const acc = zoneBounds.get(key) ?? L.latLngBounds([]);
      acc.extend((layer as L.GeoJSON).getBounds());
      zoneBounds.set(key, acc);
    });
    zoneBounds.forEach((bounds, key) => {
      const center = bounds.getCenter();
      this.zoneCentroids.set(key, center);
      const name = MANAUS_ZONA_DISPLAY_NAME[key] ?? key;
      this.zoneLabelMarkers.push(this.makeLabelMarker(center, 'exp-zone-label', name));
    });
  }

  // ── marcadores de POI + acoes ───────────────────────────────────────────────

  /** Monta os grupos de POI/acoes e liga o overlay de votos (centroides ja prontos). */
  private buildMarkers(): void {
    this.poiGroups = buildPoiGroups(this.munCentroids, (id) => this.munName(id));
    this.campaignGroups = buildCampaignGroups(this.munCentroids, (id) => this.munName(id));
    this.acoesGroup = buildAcoesGroup(this.munCentroids);
    // this.map ja existe: createMap roda antes de loadGeoAndBuild -> buildMarkers.
    this.voteLayer.bind(this.map as LeafletMap, this.munCentroids, this.zoneCentroids);
  }

  /** Repinta o overlay de votos com o snapshot atual (delega ao ExploreVoteLayer). */
  private renderVoteLayer(): void {
    const g26 = this.g26.selection();
    const g26Rows = g26 ? deriveG26VoteRows(g26, this.candidate) : null;
    this.voteLayer.render({ voteViz: this.voteViz, year: this.year, candidate: this.candidate, level: this.voteLevel, g26Rows });
  }

  /** Abre o popup de ranking (Leaflet) no centroide da regiao clicada. */
  private openVotePopup(kind: ExploreSelectionKind, id: string): void {
    const g26Html = kind === 'mun' ? this.g26.votePopupHtml(id, this.munName(id), this.candidate) : null;
    const g26Center = this.munCentroids.get(id);
    if (g26Html && g26Center && this.map) {
      L.popup({ ...EXPLORE_TALL_POPUP_OPTIONS }).setLatLng(g26Center).setContent(g26Html).openOn(this.map);
      return;
    }
    const center = kind === 'mun' ? this.munCentroids.get(id) : this.zoneCentroids.get(id as ZonaKey);
    const dist = kind === 'mun' ? munDistribution(id, this.year) : zoneDistribution(id as ZonaKey, this.year);
    if (!center || !dist || !this.map) return;
    const total = dist.reduce((acc, entry) => acc + entry.votos, 0);
    const name = kind === 'mun' ? this.munName(id) : `Zona ${MANAUS_ZONA_DISPLAY_NAME[id as ZonaKey] ?? id}`;
    const html = buildVoteRankingHtml(dist, name, total, this.candidate);
    L.popup({ className: 'exp-vote-popup', maxWidth: 320, autoPan: true }).setLatLng(center).setContent(html).openOn(this.map);
  }

  /** Nome de um municipio (dataset -> munNames -> id) (assets/explore.js 271). */
  munName(id: MunicipalityId): string {
    return EXPLORE_DATASET.munis[id]?.name ?? MANAUS_MUN_NAMES[id] ?? id;
  }

  /** Re-pinta camadas com as styleFns atuais e realca a selecao (assets/explore.js 311-319). */
  private render(): void {
    this.munLayer?.setStyle((f) => this.munStyle(munGeoFeatureId(f as MunFeature)));
    this.zoneLayer?.setStyle((f) => this.zoneStyle(bairroZonaKey(f as BairroFeature)));
    this.stateOutline?.bringToFront();
    if (this.selectedLayer) {
      (this.selectedLayer as L.Path).setStyle({ weight: 2.5, color: '#1F1F1F', fillOpacity: 0.84, opacity: 1 });
      (this.selectedLayer as L.GeoJSON).bringToFront();
    }
    this.renderVoteLayer();
  }

  // ── troca de nivel por zoom ──────────────────────────────────────────────────

  /** True quando o zoom esta sobre Manaus alem do limiar (mostra zonas). */
  private shouldShowZones(): boolean {
    // Modo 2026: as zonas administrativas nao tem resultado oficial; o detalhe de
    // Manaus vem das ZONAS ELEITORAIS (camada TSE pintada) e dos locais de votacao.
    if (this.g26.active) return false;
    const map = this.map as LeafletMap;
    const overManaus = this.munBounds.get(MANAUS_ID)?.contains(map.getCenter()) ?? false;
    return map.getZoom() >= EXPLORE_ZOOM_THRESHOLD && overManaus;
  }

  /** Alterna municipios <-> zonas (choropleto + overlay de votos) conforme o zoom. */
  private updateForZoom(): void {
    if (!this.map || !this.munLayer || !this.zoneLayer) return;
    const showZones = this.shouldShowZones();
    if (showZones) this.showZoneLevel();
    else this.showMunLevel();
    this.syncVoteLevel(showZones ? 'zone' : 'mun');
  }

  /** Re-renderiza o overlay de votos so quando o NIVEL muda (evita re-pop a cada pan). */
  private syncVoteLevel(level: VoteLevel): void {
    if (level === this.voteLevel) return;
    this.voteLevel = level;
    this.renderVoteLayer();
  }

  /** Nivel Manaus: esconde municipios/labels, mostra zonas/labels. */
  private showZoneLevel(): void {
    const map = this.map as LeafletMap;
    if (this.munLayer && map.hasLayer(this.munLayer)) map.removeLayer(this.munLayer);
    this.munLabelMarkers.forEach((m) => { if (map.hasLayer(m)) map.removeLayer(m); });
    if (this.zoneLayer && !map.hasLayer(this.zoneLayer)) this.zoneLayer.addTo(map);
    this.zoneLabelMarkers.forEach((m) => { if (!map.hasLayer(m)) m.addTo(map); });
  }

  /** Nivel estadual: esconde zonas/labels, mostra municipios/labels. */
  private showMunLevel(): void {
    const map = this.map as LeafletMap;
    if (this.zoneLayer && map.hasLayer(this.zoneLayer)) map.removeLayer(this.zoneLayer);
    this.zoneLabelMarkers.forEach((m) => { if (map.hasLayer(m)) map.removeLayer(m); });
    if (this.munLayer && !map.hasLayer(this.munLayer)) this.munLayer.addTo(map);
    this.munLabelMarkers.forEach((m) => { if (!map.hasLayer(m)) m.addTo(map); });
  }

  // ── selecao ──────────────────────────────────────────────────────────────────

  /** Seleciona uma entidade (exclusiva), reseta a anterior e re-pinta. */
  select(kind: ExploreSelectionKind, id: string, layer: Layer | null): void {
    this.resetSelectedLayer();
    this.selectedKind = kind;
    this.selectedId = id;
    this.selectedLayer = layer;
    this.render();
  }

  /**
   * Seleciona um municipio pelo id: acha a layer correspondente na camada de
   * municipios, destaca-a de verdade (render aplica o estilo de selecao) e voa ate
   * ela. Usado pela SELECAO LIGADA do painel Comparar (clique no ponto/ranking) —
   * antes passava layer nula e nao destacava nada.
   */
  selectMunById(id: MunicipalityId): void {
    if (this.munLayer && this.map && !this.map.hasLayer(this.munLayer)) this.showMunLevel();
    let target: Layer | null = null;
    this.munLayer?.eachLayer((layer) => {
      if ((layer as FeaturedLayer<MunFeature>).feature.properties.id === id) target = layer;
    });
    this.select('mun', id, target);
    if (target) (target as L.GeoJSON).bringToFront();
    this.flyToMun(id);
  }

  /** Limpa a selecao atual e re-pinta (assets/explore.js 349-356). */
  clearSelection(): void {
    this.resetSelectedLayer();
    this.selectedKind = null;
    this.selectedId = null;
    this.selectedLayer = null;
    this.render();
  }

  /** Restaura o estilo da layer selecionada na sua camada de origem. */
  private resetSelectedLayer(): void {
    if (!this.selectedLayer) return;
    if (this.selectedKind === 'mun') this.munLayer?.resetStyle(this.selectedLayer);
    else this.zoneLayer?.resetStyle(this.selectedLayer);
  }

  // ── navegacao / camadas (chamados pela UI) ───────────────────────────────────

  /** Enquadra todo o estado do AM (assets/explore.js 358). */
  fitState(): void {
    if (this.munLayer && this.map) this.map.fitBounds(this.munLayer.getBounds(), { padding: [24, 24] });
  }

  /** Voa para os bounds de uma regiao (assets/explore.js 359-362). */
  flyToRegion(region: AmazonasRegion): void {
    const bounds = this.regionBounds.get(region);
    if (bounds && this.map) this.map.flyToBounds(bounds, { duration: prefersReducedMotion() ? 0 : 1.0, padding: [30, 30] });
  }

  /** Voa para os bounds de um municipio (maxZoom 11 p/ Manaus, 9 demais). */
  flyToMun(id: MunicipalityId): void {
    const bounds = this.munBounds.get(id);
    if (bounds && this.map) this.map.flyToBounds(bounds, { duration: prefersReducedMotion() ? 0 : 1.0, maxZoom: id === MANAUS_ID ? 11 : 9 });
  }

  /** Re-mede o mapa apos a abertura da rota (assets/explore.js 367). */
  private invalidate(): void {
    const map = this.map;
    if (map) setTimeout(() => map.invalidateSize(), 30);
  }

  /** Liga/desliga o grupo de POIs de uma categoria (assets/explore.js 374). */
  togglePoi(cat: ExplorePoiCategory, on: boolean): void {
    const group = this.poiGroups.get(cat);
    if (!group || !this.map) return;
    if (on) group.addTo(this.map);
    else this.map.removeLayer(group);
  }

  /** Liga/desliga o grupo de acoes de campo (assets/explore.js 375). */
  toggleAcoes(on: boolean): void {
    if (!this.acoesGroup || !this.map) return;
    if (on) this.acoesGroup.addTo(this.map);
    else this.map.removeLayer(this.acoesGroup);
  }

  /** Liga/desliga um grupo de marcadores de campanha (espelha togglePoi). */
  toggleCampaign(kind: ExploreCampaignMarkerKind, on: boolean): void {
    const group = this.campaignGroups.get(kind);
    if (!group || !this.map) return;
    if (on) group.addTo(this.map);
    else this.map.removeLayer(group);
  }

  /** Liga/desliga a camada de zonas eleitorais do TSE; false = asset falhou (UI reverte o switch). */
  toggleTseZones(on: boolean): Promise<boolean> {
    if (!this.map) return Promise.resolve(false);
    this.tseZoneLayer ??= new ExploreTseZoneLayer(this.map, fetchTseZones(GEO_TSE_ZONES_URL));
    this.tseZoneLayer.setResultPort(this.g26.tseResultPort());
    return this.tseZoneLayer.setVisible(on);
  }

  /** Liga/desliga os locais de votacao 2026 (TSE); false = asset falhou (UI reverte). */
  toggleG26Locais(on: boolean): Promise<boolean> {
    return this.map ? this.g26.toggleLocais(this.map, on) : Promise.resolve(false);
  }

  // ── setters (UI) ─────────────────────────────────────────────────────────────

  /**
   * Entra no modo 2026 com o cargo dado (ou troca de cargo). Recortes de partido/2º e
   * candidato sao zerados: siglas/candidatos de outro cargo/ano nao se aplicam.
   * Resolve false se o asset nao carregar (mapa segue no ano historico).
   */
  async setElection2026(cargo: G26CargoCode): Promise<boolean> {
    const ok = await this.g26.enter(cargo);
    if (ok) this.applyElectionChange();
    return ok;
  }

  /** Zera recortes que dependem de ano/cargo e repinta tudo (camadas TSE inclusive). */
  private applyElectionChange(): void {
    this.party = null;
    this.runnerUp = null;
    this.candidate = null;
    this.g26.syncLayers(this.tseZoneLayer);
    this.updateForZoom();
    this.render();
    this.ui?.refresh();
  }

  /** Troca a metrica de coloracao, re-pinta e atualiza a UI (assets/explore.js 370). */
  setColorBy(metric: ExploreColorMetric): void {
    this.colorBy = metric;
    this.render();
    this.ui?.refresh();
  }

  /** Troca o ano ativo (assets/explore.js 371). */
  setYear(year: ElectoralYear): void {
    if (this.g26.active) { this.g26.leave(); this.year = year; this.applyElectionChange(); return; }
    this.year = year;
    this.render();
    this.ui?.refresh();
  }

  /** Troca o filtro de partido (assets/explore.js 372). */
  setParty(party: string | null): void {
    this.party = party || null;
    this.render();
    this.ui?.refresh();
  }

  /** Troca o filtro de regiao e voa pra ela (ou re-enquadra o estado) (assets/explore.js 373). */
  setRegion(region: AmazonasRegion | null): void {
    this.region = region || null;
    this.render();
    if (region) this.flyToRegion(region);
    else this.fitState();
    this.ui?.refresh();
  }

  /** Recorte por microrregiao — esmaece os demais (decisao de produto 2026-05-31). */
  setMicro(micro: string | null): void {
    this.micro = micro || null;
    this.render();
    this.ui?.refresh();
  }

  /** Recorte por faixa de eleitorado (decisao de produto 2026-05-31). */
  setEleitoradoBand(band: string | null): void {
    this.eleitoradoBand = band || null;
    this.render();
    this.ui?.refresh();
  }

  /** Recorte por 2º colocado — esmaece quem nao tem o partido como vice (2026-05-31). */
  setRunnerUp(party: string | null): void {
    this.runnerUp = party || null;
    this.render();
    this.ui?.refresh();
  }

  /**
   * Troca o modo de visualizacao de votos (overlay estilo Datapedia). Ao ativar um
   * modo (≠ 'nenhum') forca a base limpa (colorBy='nenhum') p/ bolhas/calor lerem com
   * clareza — decisao de produto: base neutra sob o overlay.
   */
  setVoteViz(mode: ExploreVoteViz): void {
    this.voteViz = mode;
    if (mode !== 'nenhum') this.colorBy = 'nenhum';
    this.render();
    this.ui?.refresh();
  }

  /**
   * Troca o candidato do overlay (id sintetico; null = agregado/vencedor).
   * fix 2026-06-09: escolher um candidato sem overlay ativo nao mostrava nada;
   * agora liga o modo padrao (votos absolutos) p/ dar feedback visual imediato.
   */
  setCandidate(id: string | null): void {
    // Id 2026 (`g26:N`) so vale no modo 2026; id sintetico so fora dele.
    const isG26Id = g26IndexOfId(id || null) !== null;
    this.candidate = id && isG26Id === this.g26.active ? id : null;
    if (this.candidate && this.voteViz === 'nenhum') { this.voteViz = 'absolutos'; this.colorBy = 'nenhum'; }
    this.render();
    this.ui?.refresh();
  }

  // ── legenda ──────────────────────────────────────────────────────────────────

  /** Bloco de legenda da metrica ativa (delega ao calculo puro com o snapshot). */
  getLegend(): ExploreLegend {
    return buildExploreLegend(this.styleState());
  }
}

/**
 * Singleton do mapa Explorar — substitui o `var ExploreMap`/`window.ExploreMap`
 * legado. O ExploreRoute chama ensure() na abertura; a UI usa os setters/getLegend.
 */
export const ExploreMap = new AmazonasExploreMap();
